import React, { useEffect, useMemo, useState } from "react";
import SlideoverModal from "../../../components/SlideoverModal";
import { FormInput, FormLabel, FormSwitch } from "../../../base-components/Form";
import Lucide from "../../../base-components/Lucide";
import Button from "../../../base-components/Button";
import { createUser, updateUser } from "src/stores/user";
import { fetchRoles } from "src/stores/role";
import { fetchLocations } from "src/stores/location";
import { RootState } from "src/stores";
import { connect } from "react-redux";
import {
  authUserProps,
  createUpdateUserProps,
  locationProps,
  roleProps,
} from "src/interfaces";
import { Eye, EyeOff } from "lucide-react";
import ReactSelect from "src/base-components/ReactSelect";
import Select from "react-select";
import { userTypes } from "./constants";
import { fetchCallLogsGetExtension } from "src/stores/calllogs";
import { MultipleSelector } from "src/components/multiple-selector";
import { getAllLocationsWithUserStatusService } from "src/services/allLocations";

type MultiOption = { id: string | number; label: string; value: string | number };

type ExtensionOption = {
  value: string | number | null;
  label: string;
  extensionNumber?: string;
};

const filterByExtensionNumber = (
  { data }: { data: ExtensionOption },
  input: string
) => {
  const search = input.trim();
  if (!search) return true;
  return !!data.extensionNumber?.includes(search);
};

const getSelectId = (opt: any) => {
  if (!opt) return null;
  if (typeof opt === "object") return opt.value ?? opt.id ?? null;
  return opt;
};

// safer cleanup (doesn't delete 0/false)
const isEmptyValue = (v: any) => v === null || v === undefined || v === "";

const Component = ({
  visible,
  setVisible,
  fetchLocations,
  locations,
  fetchRoles,
  roles,
  model = {} as any,
  setModel = () => { },
  onCreate,
  onUpdate,
  onCreateUpdateComplete,
  authUser,
  fetchGetExtension,
  callLogsGetExtension,
}: {
  visible: boolean;
  setVisible: Function;
  fetchLocations: Function;
  locations: locationProps[];
  fetchRoles: Function;
  roles: roleProps;
  model?: createUpdateUserProps;
  setModel?: Function;
  onCreate: (payload: createUpdateUserProps) => void;
  onUpdate: (payload: createUpdateUserProps) => void;
  onCreateUpdateComplete: Function;
  authUser: authUserProps;
  fetchGetExtension: Function;
  callLogsGetExtension?: any;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showExtensionMsg, setShowExtensionMsg] = useState("");

  // ✅ single location id (real id)
  const [singleLocationId, setSingleLocationId] = useState<string | number | null>(null);

  // ✅ multi location ids (MUST NOT contain singleLocationId)
  const [selectedUserLocations, setSelectedUserLocations] = useState<(string | number)[]>([]);
  const [rawLocationOptions, setRawLocationOptions] = useState<MultiOption[]>([]);
  const [loadingLocationStatus, setLoadingLocationStatus] = useState(false);

  const roleList = (roles as any)?.data || [];
  const roleOptions = roleList?.map((item: any) => ({
    value: item.id,
    label: item.name,
  }));

  const locationOptions = (locations || []).map((item: any) => ({
    value: item.id,
    label: item.name,
  }));

  const extensionOptions = useMemo<ExtensionOption[]>(() => {
    return [
      { value: null, label: "None" },
      ...(callLogsGetExtension?.data?.result?.map((item: any) => ({
        value: item.extension_id,
        label: `${item.extension_number} ${item?.user?.email ?? ""}`,
        extensionNumber: String(item.extension_number ?? ""),
      })) || []),
    ];
  }, [callLogsGetExtension]);

  // ✅ remove single-selected location from multi options list (so user cannot pick it)
  const multiOptions = useMemo(() => {
    if (!singleLocationId) return rawLocationOptions;
    return rawLocationOptions.filter((o) => o.value !== singleLocationId);
  }, [rawLocationOptions, singleLocationId]);

  const loadLocationsWithUserStatus = async () => {
    try {
      setLoadingLocationStatus(true);
      const res: any = await getAllLocationsWithUserStatusService();
      const result = res?.result || res?.data?.result || [];

      const formatted: MultiOption[] = result.map((item: any) => ({
        id: item?.location?.id,
        value: item?.location?.id,
        label: item?.location?.name,
      }));

      setRawLocationOptions(formatted);

      // optional: preselect assigned (but do NOT include singleLocationId here)
      // const preSelected = result
      //   .filter((x: any) => x?.has_location)
      //   .map((x: any) => x?.location?.id);

      // setSelectedUserLocations(preSelected);

      // setModel((prev: any) => ({ ...prev, locations: preSelected }));

      const secondaryIds =
        model?.user_locations?.map((item: any) => item.location_id) || [];

      setSelectedUserLocations(secondaryIds);

      setModel((prev: any) => ({
        ...prev,
        locations: secondaryIds,
      }));
    } catch (e) {
      console.error("getAllLocationsWithUserStatusService failed", e);
    } finally {
      setLoadingLocationStatus(false);
    }
  };

  useEffect(() => {
    if (!visible) return;

    setLoadingRoles(true);
    setLoadingLocations(true);
      fetchRoles({
        callback: () => {
          setLoadingRoles(false);
        },
      });
      fetchLocations({
        callback: () => {
          setLoadingLocations(false);
        },
      });
    fetchGetExtension();
    loadLocationsWithUserStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // ✅ edit mode: if model.location_id exists, sync singleLocationId and REMOVE it from multi list
  useEffect(() => {
    const id = getSelectId((model as any)?.location_id);
    if (!id) return;

    setSingleLocationId(id);

    setSelectedUserLocations((prev) => {
      const next = (prev || []).filter((x) => x !== id);
      setModel((p: any) => ({ ...p, locations: next }));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(model as any)?.location_id]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);

    let payload: any = { ...model };

    // ✅ ALWAYS normalize location_id -> ID
    const normalizedLocationId =
      getSelectId(payload.location_id) ?? singleLocationId ?? null;

    if (normalizedLocationId != null) payload.location_id = normalizedLocationId;

    // ✅ locations MUST NOT include location_id
    payload.locations = (selectedUserLocations || []).filter(
      (id) => id !== normalizedLocationId
    );

    // ✅ UPDATE
    if (payload?.id) {
      Object.keys(payload).forEach((key) => {
        if (isEmptyValue(payload[key])) delete payload[key];
      });

      const finalPayload = payload?.chat_access ? payload : { ...payload, chat_access: 0 };

      onUpdate({
        ...finalPayload,
        callback: (isError: boolean) => {
          setVisible(!!isError);
          setSubmitting(false);
          onCreateUpdateComplete && onCreateUpdateComplete(isError);
        },
      } as any);
      return;
    }

    Object.keys(payload)?.forEach((key) => {
      if (key !== "extension_id" && isEmptyValue(payload[key])) delete payload[key];
    });
    onCreate({
      ...payload,
      callback: (isError: boolean) => {
        setVisible(!!isError);
        setSubmitting(false);
        onCreateUpdateComplete && onCreateUpdateComplete(isError);
      },
    } as any);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event?.target?.name;
    const value = event?.target?.value;
    setModel({
      ...model,
      [name]: value,
    });
  };

  return (
    <SlideoverModal
      size="md"
      headerTitle={model?.id ? "Update User" : "Create New User"}
      visible={visible}
      onClose={(value) => {
        setVisible(value);
      }}
    >
      <form onSubmit={onSubmit} className="flex flex-col">
        <div className="w-full flex gap-2">
          <div className="w-1/2">
            <FormLabel>First Name</FormLabel>
            <FormInput
              w-full
              onChange={onChange}
              value={model?.name || ""}
              required
              name="name"
              type="text"
              className="rounded-md"
              placeholder={"Enter first name"}
            />
          </div>
          <div className="w-1/2">
            <FormLabel>Last Name</FormLabel>
            <FormInput
              onChange={onChange}
              value={model?.last_name || ""}
              name="last_name"
              type="text"
              className="rounded-md"
              placeholder={"Enter last name"}
            />
          </div>
        </div>
        <div className="w-full mt-3">
          <FormLabel>Username</FormLabel>
          <FormInput
            required
            onChange={onChange}
            value={model?.username || ""}
            type="text"
            name="username"
            placeholder={"Enter username"}
          />
        </div>
        <div className="w-full mt-3">
          <FormLabel>Email</FormLabel>
          <FormInput
            required
            onChange={onChange}
            value={model?.email || ""}
            type="email"
            name="email"
            placeholder={"Enter email"}
          />
        </div>
        <div className="w-full mt-3 relative">
          <FormLabel>{model?.id ? "Password (Optional)" : "Password"}</FormLabel>
          <FormInput
            onChange={onChange}
            value={model?.password || ""}
            type={showPassword ? "text" : "password"}
            name="password"
            autoComplete="off"
            placeholder={"Enter password"}
            required={model.id ? false : true}
          />
          {showPassword ? (
            <EyeOff
              className="absolute bottom-2 right-2 cursor-pointer w-5 text-gray-500"
              onClick={() => setShowPassword(false)}
            />
          ) : (
            <Eye
              className="absolute bottom-2 right-2 cursor-pointer w-5 text-gray-500"
              onClick={() => setShowPassword(true)}
            />
          )}
        </div>
        {authUser?.user_type &&
          (authUser.user_type.includes("Admin") ||
            authUser.user_type.includes("Superadmin")) && (
            <div className="mt-2 ">
              <FormLabel>User Type</FormLabel>
              <ReactSelect
                required
                options={userTypes}
                onChange={(opt: any) => {
                  console.log("")
                  setModel((prev: any) => ({ ...prev, user_type: opt.value }));
                }}
                value={model?.user_type}
                placeholder="Select Type"
              />
            </div>
          )}
        <div className="mt-2 ">
          <FormLabel>Role</FormLabel>
          <ReactSelect
            required
            options={roleOptions}
            loading={loadingRoles}
            onChange={(opt: any) => {
              setModel((prev: any) => ({ ...prev, role_id: opt.value }));
            }}
            value={model?.role_id}
            placeholder="Select Role"
          />
        </div>

        {/* ✅ SINGLE LOCATION */}
        <div className="mt-2">
          <FormLabel>Location (Single)</FormLabel>
          <ReactSelect
            required
            options={locationOptions}
            loading={loadingLocations}
            onChange={(opt: any) => {
              const id = getSelectId(opt);

              setSingleLocationId(id);

              // keep option object for UI
              setModel((prev: any) => ({
                ...prev,
                location_id: opt,
              }));

              // ✅ ensure selectedUserLocations does NOT contain this id
              if (id != null) {
                setSelectedUserLocations((prev) => {
                  const next = (prev || []).filter((x) => x !== id);
                  setModel((p: any) => ({ ...p, locations: next }));
                  return next;
                });
              }
            }}
            value={model?.location_id}
            placeholder="Please select office location."
          />
        </div>

        {/* ✅ MULTI LOCATIONS (must exclude location_id) */}
        <div className="mt-3">
          <FormLabel>User Locations (Multiple)</FormLabel>
          <MultipleSelector
            options={multiOptions}
            selected={selectedUserLocations}
            onChange={(values) => {
              const filtered =
                singleLocationId != null
                  ? values.filter((id) => id !== singleLocationId)
                  : values;

              setSelectedUserLocations(filtered);
              setModel((prev: any) => ({ ...prev, locations: filtered }));
            }}
            placeholder={loadingLocationStatus ? "Loading locations..." : "Select locations..."}
            searchable
            clearable
          />
        </div>

        {/* EXTENSION */}
        <div className="mt-2">
          <FormLabel>Extension ID</FormLabel>
          <Select
            isClearable
            required
            options={extensionOptions}
            filterOption={filterByExtensionNumber}
            noOptionsMessage={() => "No matching extension number"}
            onChange={(selectedOption: any) => {
              const selectedValue = selectedOption?.value ?? null;
              setModel((prev: any) => ({ ...prev, extension_id: selectedValue }));
              setShowExtensionMsg("");

              if (!selectedValue) return;

              const selectedExtension = callLogsGetExtension?.data?.result?.find(
                (item: any) => item.extension_id === selectedValue
              );

              if (selectedExtension?.user) {
                setShowExtensionMsg(
                  "Extension already in use. Assigning this extension will remove it from the current user and transfer it to this account."
                );
              }
            }}
            value={extensionOptions.find((o: any) => o.value === model?.extension_id) || null}
            placeholder="Please select Extension ID"
          />
          {showExtensionMsg && (
            <p className="text-sm text-orange-600 mt-2">{showExtensionMsg}</p>
          )}
        </div>

        {model?.id && (
          <div className="w-full mt-3">
            <FormLabel>Status</FormLabel>
            <ReactSelect
              options={[
                { value: "Active", label: "Active" },
                { value: "Inactive", label: "Inactive" },
              ]}
              value={model?.status}
              onChange={(opt: any) => setModel((prev: any) => ({ ...prev, status: opt }))}
            />
          </div>
        )}

        <div className="mt-3">
          <FormSwitch>
            <FormSwitch.Input
              value={model?.chat_access}
              checked={model?.chat_access == 1}
              onChange={() =>
                setModel((prev: any) => ({
                  ...prev,
                  chat_access: model?.chat_access === 0 ? 1 : 0,
                }))
              }
              type="checkbox"
            />
            <FormSwitch.Label>Chat Access</FormSwitch.Label>
          </FormSwitch>
        </div>

        <div className="absolute bottom-0 right-0 py-5 px-7">
          <Button
            className="w-full px-10 md:w-auto text-white bg-primary mt-auto ml-auto"
            type="submit"
            disabled={submitting}
            loading={submitting}
          >
            <Lucide icon="Check" className="stroke-[1.3] w-4 h-4 mr-2 -ml-2" />
            Submit
          </Button>
        </div>
      </form>
    </SlideoverModal>
  );
};

const mapStateToProps = ({ roles, locations, authUser, callLogsGetExtension }: RootState) => ({
  roles,
  locations,
  authUser,
  callLogsGetExtension,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchRoles: (payload: any) => dispatch(fetchRoles(payload)),
  fetchLocations: (payload: any) => dispatch(fetchLocations(payload)),
  fetchGetExtension: () => dispatch(fetchCallLogsGetExtension()),
  onCreate: (payload: any) => dispatch(createUser(payload)),
  onUpdate: (payload: any) => dispatch(updateUser(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Component);


// import React, { useEffect, useState } from "react";
// import SlideoverModal from "../../../components/SlideoverModal";
// import {
//   FormInput,
//   FormLabel,
//   FormSelect,
//   FormSwitch,
// } from "../../../base-components/Form";
// import Lucide from "../../../base-components/Lucide";
// import Button from "../../../base-components/Button";
// import _ from "lodash";
// import { createUser, updateUser } from "src/stores/user";
// import { fetchRoles } from "src/stores/role";
// import { fetchLocations } from "src/stores/location";
// import { RootState } from "src/stores";
// import { connect } from "react-redux";
// import {
//   authUserProps,
//   createUpdateUserProps,
//   locationProps,
//   roleProps,
// } from "src/interfaces";
// import { Eye, EyeOff } from "lucide-react";
// import ReactSelect from "src/base-components/ReactSelect";
// import Select from "react-select";
// import LoadingIcon from "src/base-components/LoadingIcon";
// import { userTypes } from "./constants";
// import { fetchCallLogsGetExtension } from "src/stores/calllogs";

// const Component = ({
//   visible,
//   setVisible,
//   fetchLocations,
//   locations,
//   fetchRoles,
//   roles,
//   model = {} as any,
//   setModel = () => {},
//   onCreate,
//   onUpdate,
//   onCreateUpdateComplete,
//   authUser,
//   fetchGetExtension,
//   callLogsGetExtension,
// }: {
//   visible: boolean;
//   setVisible: Function;
//   fetchLocations: Function;
//   locations: locationProps[];
//   fetchRoles: Function;
//   roles: roleProps;
//   model?: createUpdateUserProps;
//   setModel?: Function;
//   onCreate: (payload: createUpdateUserProps) => void;
//   onUpdate: (payload: createUpdateUserProps) => void;
//   onCreateUpdateComplete: Function;
//   authUser: authUserProps;
//   fetchGetExtension: Function;
//   callLogsGetExtension?: any;
// }) => {
//   const [submitting, setSubmitting] = useState(false);
//   const [loadingLocations, setLoadingLocations] = useState(true);
//   const [loadingRoles, setLoadingRoles] = useState(true);
//   const [showPassword, setShowPassword] = useState(false);
//   const [showExtensionMsg, setShowExtensionMsg] = useState("");

//   const roleList = roles?.data || [];
//   const roleOptions = roleList?.map((item) => ({
//     value: item.id,
//     label: item.name,
//   }));
//   const locationOptions = locations.map((item) => ({
//     value: item.id,
//     label: item.name,
//   }));
//   const extensionOptions = [
//     { value: null, label: "None" },
//     ...(callLogsGetExtension?.data?.result?.map((item: any) => ({
//       value: item.extension_id,
//       label: `${item.extension_number} ${item?.user?.email ?? ""}`,
//     })) || []),
//   ];

//   useEffect(() => {
//     if (visible) {
//       setLoadingRoles(true);
//       setLoadingLocations(true);
//       fetchRoles({
//         callback: () => {
//           setLoadingRoles(false);
//         },
//       });
//       fetchLocations({
//         callback: () => {
//           setLoadingLocations(false);
//         },
//       });
//       fetchGetExtension();
//     }
//   }, [visible]);

//   const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
//     event.preventDefault();
//     setSubmitting(true);
//     let payload: any = model;
//     if (payload?.id) {
//       Object?.keys(payload).map((key) => {
//         if (!payload[key]) {
//           delete payload[key];
//         }
//       });

//       let finalPayload: any;
//       if (!payload?.chat_access) {
//         finalPayload = { ...payload, chat_access: 0 };
//       }

//       finalPayload = payload;

//       onUpdate({
//         ...finalPayload,
//         callback: (isError: boolean) => {
//           setVisible(!!isError);
//           setSubmitting(false);
//           onCreateUpdateComplete && onCreateUpdateComplete(isError);
//         },
//       } as any);
//       return;
//     }

//     Object.keys(payload)?.forEach((key) => {
//       if (key !== "extension_id" && !payload[key]) {
//         delete payload[key];
//       }
//     });
//     onCreate({
//       ...payload,
//       callback: (isError: boolean) => {
//         setVisible(!!isError);
//         setSubmitting(false);
//         onCreateUpdateComplete && onCreateUpdateComplete(isError);
//       },
//     } as any);
//   };

//   const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const name = event?.target?.name;
//     const value = event?.target?.value;
//     setModel({
//       ...model,
//       [name]: value,
//     });
//   };

//   return (
//     <SlideoverModal
//       size="md"
//       headerTitle={model?.id ? "Update User" : "Create New User"}
//       visible={visible}
//       onClose={(value) => {
//         setVisible(value);
//       }}
//     >
//       <form onSubmit={onSubmit} className="flex flex-col">
//         <div className="w-full flex gap-2">
//           <div className="w-1/2">
//             <FormLabel>First Name</FormLabel>
//             <FormInput
//               w-full
//               onChange={onChange}
//               value={model?.name || ""}
//               required
//               name="name"
//               type="text"
//               className="rounded-md"
//               placeholder={"Enter first name"}
//             />
//           </div>
//           <div className="w-1/2">
//             <FormLabel>Last Name</FormLabel>
//             <FormInput
//               onChange={onChange}
//               value={model?.last_name || ""}
//               name="last_name"
//               type="text"
//               className="rounded-md"
//               placeholder={"Enter last name"}
//             />
//           </div>
//         </div>
//         <div className="w-full mt-3">
//           <FormLabel>Username</FormLabel>
//           <FormInput
//             required
//             onChange={onChange}
//             value={model?.username || ""}
//             type="text"
//             name="username"
//             placeholder={"Enter username"}
//           />
//         </div>
//         <div className="w-full mt-3">
//           <FormLabel>Email</FormLabel>
//           <FormInput
//             required
//             onChange={onChange}
//             value={model?.email || ""}
//             type="email"
//             name="email"
//             placeholder={"Enter email"}
//           />
//         </div>
//         <div className="w-full mt-3 relative">
//           <FormLabel>{model.id ? "Password (Optional)" : "Password"}</FormLabel>
//           <FormInput
//             onChange={onChange}
//             value={model?.password || ""}
//             type={showPassword ? "text" : "password"}
//             name="password"
//             autoComplete="off"
//             placeholder={"Enter password"}
//             required={model.id ? false : true}
//           />
//           {showPassword ? (
//             <EyeOff
//               className="absolute bottom-2 right-2 cursor-pointer w-5 text-gray-500"
//               onClick={() => setShowPassword(false)}
//             />
//           ) : (
//             <Eye
//               className="absolute bottom-2 right-2 cursor-pointer w-5 text-gray-500"
//               onClick={() => setShowPassword(true)}
//             />
//           )}
//         </div>
//         {authUser?.user_type &&
//           (authUser.user_type.includes("Admin") ||
//             authUser.user_type.includes("Superadmin")) && (
//             <div className="mt-2 ">
//               <FormLabel>User Type</FormLabel>
//               <div className="w-full">
//                 <ReactSelect
//                   required={true}
//                   options={userTypes}
//                   onChange={(_: any, value) => {
//                     setModel((previous) => ({
//                       ...previous,
//                       user_type: value,
//                     }));
//                   }}
//                   value={model?.user_type}
//                   placeholder="Select Type"
//                 />
//               </div>
//             </div>
//           )}
//         <div className="mt-2 ">
//           <FormLabel>Role</FormLabel>
//           <div className="w-full">
//             <ReactSelect
//               required={true}
//               options={roleOptions}
//               loading={loadingRoles}
//               onChange={(_: any, value) => {
//                 setModel((previous) => ({
//                   ...previous,
//                   role_id: value,
//                 }));
//               }}
//               value={model?.role_id}
//               placeholder="Select Role"
//             />
//           </div>
//         </div>
//         <div className="mt-2">
//           <FormLabel>Location</FormLabel>
//           <div className="w-full">
//             <ReactSelect
//               required={true}
//               options={locationOptions}
//               loading={loadingLocations}
//               onChange={(_: any, value) => {
//                 setModel((previous) => ({
//                   ...previous,
//                   location_id: value,
//                 }));
//               }}
//               value={model?.location_id}
//               placeholder="Please select office location."
//             />
//           </div>
//         </div>
//         <div className="mt-2">
//           <FormLabel>Extension ID</FormLabel>
//           <div className="w-full">
//             <Select
//               isClearable
//               required
//               options={extensionOptions}
//               onChange={(selectedOption: any) => {
//                 const selectedValue = selectedOption?.value ?? null;

//                 // Update the model
//                 setModel((prev) => ({
//                   ...prev,
//                   extension_id: selectedValue,
//                 }));

//                 setShowExtensionMsg("");

//                 if (!selectedValue) return;

//                 // Check if extension is already in use
//                 const selectedExtension =
//                   callLogsGetExtension?.data?.result?.find(
//                     (item: any) => item.extension_id === selectedValue
//                   );

//                 if (selectedExtension?.user) {
//                   setShowExtensionMsg(
//                     "Extension already in use. Assigning this extension will remove it from the current user and transfer it to this account."
//                   );
//                 }
//               }}
//               value={
//                 extensionOptions.find(
//                   (option) => option.value === model?.extension_id
//                 ) || null
//               }
//               placeholder="Please select Extension ID"
//             />
//             {showExtensionMsg && (
//               <p className="text-sm text-orange-600 mt-2">{showExtensionMsg}</p>
//             )}
//           </div>

//           {/* <div className="w-full">
//             <Select
//               required={true}
//               options={extensionOptions}
//               // loading={callLogsGetExtension?.loading}
//               onChange={(_: any, value) => {
//                 const selectedValue = value ?? null;
//                 setModel((previous) => ({
//                   ...previous,
//                   extension_id: selectedValue,
//                 }));
//                 setShowExtensionMsg("");
//                 if (selectedValue === null) return;

//                 const selectedExtension =
//                   callLogsGetExtension?.data?.result?.find(
//                     (item: any) => item.extension_id === selectedValue
//                   );

//                 if (selectedExtension?.user) {
//                   setShowExtensionMsg(
//                     "Extension already in use. Assigning this extension will remove it from the current user and transfer it to this account."
//                   );
//                 }
//               }}
//               // value={model?.extension_id ?? "None"}
//               value={
//                 extensionOptions.find(
//                   (option) => option.value === model?.extension_id
//                 ) || null
//               }
//               placeholder="Please select Extension ID"
//             />
//             {showExtensionMsg && (
//             <p className="text-sm  text-orange-600 mt-2">{showExtensionMsg}</p>
//           )}
//           </div> */}
//         </div>
//         {model.id && (
//           <div className={`${model.id ? "w-full" : "w-[10rem]"} mt-3`}>
//             <FormLabel>Status</FormLabel>
//             <ReactSelect
//               options={[
//                 { value: "Active", label: "Active" },
//                 { value: "Inactive", label: "Inactive" },
//               ]}
//               value={model?.status}
//               onChange={(_, value) => {
//                 setModel((previous) => ({
//                   ...previous,
//                   status: value,
//                 }));
//               }}
//             />
//           </div>
//         )}
//         <div className="mt-3">
//           <FormSwitch className="">
//             <FormSwitch.Input
//               value={model?.chat_access}
//               checked={model?.chat_access == 1}
//               onChange={() => {
//                 setModel((previous) => ({
//                   ...previous,
//                   chat_access: model?.chat_access === 0 ? 1 : 0,
//                 }));
//               }}
//               type="checkbox"
//             />
//             <FormSwitch.Label className=" ">Chat Access</FormSwitch.Label>
//           </FormSwitch>
//         </div>
//         <div className="absolute bottom-0 right-0 py-5 px-7">
//           <Button
//             className="w-full px-10 md:w-auto text-white bg-primary mt-auto ml-auto"
//             type="submit"
//             disabled={submitting}
//             loading={submitting}
//           >
//             <Lucide icon="Check" className="stroke-[1.3] w-4 h-4 mr-2 -ml-2" />
//             Submit
//           </Button>
//         </div>
//       </form>
//     </SlideoverModal>
//   );
// };

// const mapStateToProps = ({
//   roles,
//   locations,
//   authUser,
//   callLogsGetExtension,
// }: RootState) => ({
//   roles,
//   locations,
//   authUser,
//   callLogsGetExtension,
// });

// const mapDispatchToProps = (dispatch: any) => ({
//   fetchRoles: (payload: any) => dispatch(fetchRoles(payload)),
//   fetchLocations: (payload: any) => dispatch(fetchLocations(payload)),
//   fetchGetExtension: () => dispatch(fetchCallLogsGetExtension()),
//   onCreate: (payload: any) => dispatch(createUser(payload)),
//   onUpdate: (payload: any) => dispatch(updateUser(payload)),
// });
// export default connect(mapStateToProps, mapDispatchToProps)(Component);